import { Component, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription } from "rxjs";
import { DataTableDirective } from "angular-datatables";
import { UserDataStoreService } from "../services/user-data-store.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Router } from '@angular/router';
import { CardAcceptorConfigService } from "../services/card-acceptor-config.service";
import { CardAcceptorConfigViewModel } from "../models/card-acceptor-config-view-model";
import { PermissionEnum } from "../services/guards/permissions";
import { AuthenticationService } from "../services/authentication.service";
import { ToastrService } from "ngx-toastr";
import { ConfirmationDialogComponent } from "../partials/confirmation-dialog/confirmation-dialog.component";
import { NgxUiLoaderService } from 'ngx-ui-loader';
import {TerminalConfigViewModel} from "../models/terminal-config-view-model";
import {MerchantService} from "../services/merchant.service";
import { SearchFilterModel } from '../models/search-filter-model';

@Component({
  selector: 'app-card-acceptor-config-management',
  templateUrl: './card-acceptor-config-management.component.html',
  styleUrls: ['./card-acceptor-config-management.component.scss']
})
export class CardAcceptorConfigManagementComponent implements OnInit {
  public dtOptions: DataTables.Settings = {};
  public cardAcceptorConfigs: CardAcceptorConfigViewModel[] = [];
  public dtTrigger: Subject<boolean> = new Subject();

  @ViewChild(DataTableDirective)
  public dtElement: DataTableDirective;

  public canEdit = false;
  public canAdd = false;
  public EditView: string;
  public canView: boolean;

  public filter: SearchFilterModel = {
    search: '',
    take: 10,
    skip: 0,
    orderBy: null,
    orderDirection: 'ASC'
  };

  constructor(private _router: Router, private _userDataStore: UserDataStoreService,
    private _authenticationService: AuthenticationService, private _modalService: NgbModal,
    private _cardAcceptorConfigService: CardAcceptorConfigService,
    private _merchantService: MerchantService,
    private _toastService: ToastrService,
    private _ngxUiLoaderService: NgxUiLoaderService) {
    this.canView = this._userDataStore.hasRequiredPermissions([PermissionEnum.ViewMerchantConfiguration], []);
    if (!this.canView) {
      this._toastService.warning("You do not have the required permissions to view this page, Please contact your system administrator", "Warning");
      this._router.navigate(['/']);
    }

    this.canAdd = this._userDataStore.hasRequiredPermissions([PermissionEnum.AddMerchantConfiguration], []);

    this.canEdit = this._userDataStore.hasRequiredPermissions([PermissionEnum.EditMerchantConfiguration], []);
    if (!this.canEdit) {
      if (this.canView) {
        this.EditView = "View";
      }
    }
    else {
      this.EditView = "Edit";
    }

  }

  ngOnInit() {
    this.dtOptions = {
      pageLength: this.filter.take,
      pagingType: 'full_numbers',
      serverSide: true,
      processing: true,
      ajax: async (dataTablesParameters: any, callback) => {
        this.filter.search = dataTablesParameters.search.value;
        this.filter.take = dataTablesParameters.length;
        this.filter.skip = dataTablesParameters.start;
        this.filter.orderDirection = dataTablesParameters.order[0].dir;
        this.filter.orderBy = dataTablesParameters.columns[dataTablesParameters.order[0].column].data;
        const result = await this._cardAcceptorConfigService.GetPagedConfigs(this.filter);
        this.cardAcceptorConfigs = result.response;
        callback({
          recordsTotal: result.total,
          recordsFiltered: result.total,
          data: []
        });
      },
      responsive: true,
      columns: [
        { name: 'Merchant Config', data: 'Id' },
        { name: 'Name', data: 'Name' },
        { name:'Actions', data: 'actions', orderable: false }
      ]
    };
  }

  public async ngAfterViewInit(): Promise<void> {
    await this.init();
  }


  private async init(): Promise<void> {
    this.dtTrigger.next();
  }

  private resetDatatable(): void {
    this.cardAcceptorConfigs = [];
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next();
    });
  }


  public async  addCardAcceptorConfig(): Promise<void> {
    this._router.navigate(['management', 'card-acceptor-config', 'new']);
  }

  public async editCardAcceptorConfig(cardAccteptor: CardAcceptorConfigViewModel): Promise<void> {
    this._router.navigate(['management', 'card-acceptor-config', cardAccteptor.id]);
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  public async duplicateMerchantConfig(merchant: CardAcceptorConfigViewModel): Promise<void> {
    merchant.name = `${merchant.name.split('-copy')[0]}-copy(${merchant.id + 1})`;
    merchant.id = 1;
    this.canAdd = this._userDataStore.hasRequiredPermissions([PermissionEnum.AddMerchantConfiguration], []);
    if (!this.canAdd) {
      this._toastService.error("You do not have the required permissions to add a merchant config record.", "error");
    } else {
      const createResult = await this._cardAcceptorConfigService.Add(merchant);
      if (createResult.success === true) {
        this._toastService.success("Merchant config duplicated", "Success");
        this.cardAcceptorConfigs = await this._cardAcceptorConfigService.GetAllConfigs();
      } else {
        this._toastService.error(createResult.message, "Error");
      }
    }
  }

  public async openDuplicationConfirmationDialog(merchant: CardAcceptorConfigViewModel) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Merchant Config Duplication"
    dialogRef.componentInstance.message = "Are you sure you want to duplicate the selected merchant config?"

    const result = await dialogRef.result;
    if (result) {
      await this.duplicateMerchantConfig(merchant);
    }
  }

  public async deleteMerchantConfig(merchant: CardAcceptorConfigViewModel): Promise<void> {
    const result = await this._cardAcceptorConfigService.DeleteCardAcceptorConfig(merchant.id);
    if (result.success === true) {
      this._toastService.success(result.message, "Success");
      this.cardAcceptorConfigs = await this._cardAcceptorConfigService.GetAllConfigs();
    } else {
      this._toastService.error(result.message, "Error");
    }
  }

  public async determineDeleteDialog(config: CardAcceptorConfigViewModel) {
    const merchantDetails = await this._cardAcceptorConfigService.GetAllMerchantDetails(config.id);
    if (!!merchantDetails && merchantDetails.length === 0) {
      await this.openDeleteConfirmationDialog(config);
    } else {
      await this.openUsedDeleteFailureConfirmationDialog(merchantDetails.map(md => 'MID: ' + md.merchantNumber));
    }
  }

  public async openDeleteConfirmationDialog(merchant: CardAcceptorConfigViewModel) {

    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Merchant Config Removal"
    dialogRef.componentInstance.message = "Are you sure you want to delete the selected merchant config?"

    const result = await dialogRef.result;
    if (result) {
      await this.deleteMerchantConfig(merchant);
    }
  }

  public async openUsedDeleteFailureConfirmationDialog(merchants: string[]) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.acceptButtonText = "OK"
    dialogRef.componentInstance.title = "Merchant Config Cannot Be Removed"
    dialogRef.componentInstance.message = "This Merchant Config is currently in use and cannot be deleted.<br/><br/><b>Merchants using config:</b>"
    dialogRef.componentInstance.listMessage = merchants;
  }
}
